import React, { createContext, useContext, useEffect, useState } from 'react';
import { getFeatureFlags, FeatureFlags } from '../services/features';

interface FeatureFlagContextType {
    flags: FeatureFlags;
    isLoading: boolean;
    isFeatureEnabled: (feature: keyof FeatureFlags) => boolean;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

export const FeatureFlagProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [flags, setFlags] = useState<FeatureFlags>({} as FeatureFlags);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchFlags = async () => {
            try {
                const data = await getFeatureFlags();
                setFlags(data);
            } catch (error) {
                console.error("Failed to fetch feature flags:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchFlags();
    }, []);

    const isFeatureEnabled = (feature: keyof FeatureFlags) => {
        return !!flags[feature];
    };

    return (
        <FeatureFlagContext.Provider value={{ flags, isLoading, isFeatureEnabled }}>
            {children}
        </FeatureFlagContext.Provider>
    );
};

export const useFeatureFlags = () => {
    const context = useContext(FeatureFlagContext);
    if (context === undefined) {
        throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
    }
    return context;
};
